import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { companyInfo, advantages, services } from '../data'
import ContactForm from '../components/ContactForm'
import Lightbox from '../components/Lightbox'
import useMeta from '../hooks/useMeta'
import ScrollReveal from '../components/ScrollReveal'

export default function Home() {
  useMeta('Forside', 'Matheus Cleaning Service leverer professionel rengøring af højeste kvalitet til private hjem og erhverv i hele Storkøbenhavn. Få et uforpligtende tilbud i dag!')

  const [testimonials, setTestimonials] = useState([])
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 })
  const [testimonialIndex, setTestimonialIndex] = useState(0)

  // Fetch testimonials from CMS
  useEffect(() => {
    // Fetch Testimonials
    fetch('https://api.dev.auxo.dk/public/content/testimonials')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setTestimonials(data)
        } else {
          // Fallback if empty
          setTestimonials([
            { name: 'Mette Poulsen', role: 'Husejer, Gentofte', text: { da: 'Matheus Cleaning Service leverer en fantastisk service! De er altid punktlige, grundige og utrolig venlige. Vores hjem har aldrig været renere.' }, rating: 5 },
            { name: 'Thomas Jensen', role: 'Kontorchef, TechNordic', text: { da: 'Vi har brugt deres erhvervsrengøring i over et år nu. Deres medarbejdere er yderst professionelle, og arbejdsmiljøet har fået et mærkbart løft.' }, rating: 5 },
            { name: 'Sofie Amalie Schmidt', role: 'Travl mor, Aarhus', text: { da: 'Deres housekeeping-service har givet os vores fritid tilbage. Det er en ren luksus at komme hjem til et perfekt redt seng og skinnende rene overflader.' }, rating: 5 },
          ])
        }
      })
      .catch(() => {
        // Fallback on error
        setTestimonials([
          { name: 'Mette Poulsen', role: 'Husejer, Gentofte', text: { da: 'Matheus Cleaning Service leverer en fantastisk service! Deres medarbejdere er altid punktlige, grundige og utrolig venlige.' }, rating: 5 },
          { name: 'Thomas Jensen', role: 'Kontorchef, TechNordic', text: { da: 'Vi har brugt deres erhvervsrengøring i over et år nu. Deres medarbejdere er yderst professionelle, og arbejdsmiljøet har fået et mærkbart løft.' }, rating: 5 },
          { name: 'Sofie Amalie Schmidt', role: 'Travl mor, Aarhus', text: { da: 'Deres housekeeping-service har givet os vores fritid tilbage. Det er en ren luksus at komme hjem til et perfekt redt seng.' }, rating: 5 },
        ])
      })
  }, [])

  // Testimonial slider navigation
  const nextTestimonial = () => {
    if (testimonials.length === 0) return
    setTestimonialIndex((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    if (testimonials.length === 0) return
    setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  // Get visible testimonials based on index
  const getVisibleTestimonials = () => {
    if (testimonials.length === 0) return []
    const items = []
    for (let i = 0; i < 3; i++) {
      items.push(testimonials[(testimonialIndex + i) % testimonials.length])
    }
    return items
  }

  return (
    <div className="flex flex-col min-h-screen bg-brand-light">
      {/* Hero Section */}
      <section className="relative min-h-[680px] lg:min-h-[820px] flex items-center overflow-hidden bg-brand-navy text-white py-28 lg:py-44">
        {/* Full-width Background Image */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/friendly-woman-cleaning-scandinavian-home-hero-v3.jpg"
            alt="Professionel kvinde der udfører rengøring i et lyst skandinavisk hjem"
            className="w-full h-full object-cover object-center scale-110 transition-transform duration-10000 ease-out"
          />
          {/* Dark Overlay for legibility */}
          <div className="absolute inset-0 bg-brand-navy/75" />
        </div>

        <div className="mx-auto max-w-7xl px-6 relative z-10 w-full">
          {/* Hero Content */}
          <ScrollReveal direction="fade" duration={1000} className="max-w-3xl flex flex-col items-center text-center mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
              Skinnende rent til både private og erhverv
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              Vi leverer kompromisløs, professionel rengøring til private hjem og erhverv i hele Storkøbenhavn. Oplev den ægte følelse af renhed med Matheus Cleaning Service.
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href="#kontakt"
                className="rounded-lg bg-brand-navy px-8 py-4 text-sm font-bold text-white transition-all duration-200 hover:bg-brand-darkblue hover:shadow-lg hover:-translate-y-0.5"
              >
                Få et uforpligtende tilbud
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <ScrollReveal direction="up" duration={800} className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy tracking-tight mt-2">
              Fordele ved at samarbejde med Matheus Cleaning Service
            </h2>
            <p className="mt-4 text-brand-text-muted leading-relaxed">
              Vi stræber altid efter at levere den bedste oplevelse. Her er fire grunde til, at vores kunder vælger os igen og igen.
            </p>
          </ScrollReveal>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((adv, index) => (
              <ScrollReveal 
                key={index} 
                direction="up" 
                delay={index * 150} 
                duration={600}
                className="flex flex-col items-center text-center"
              >
                <div className="text-black mb-4 text-3xl">
                  <i className={`fa-solid ${adv.icon}`}></i>
                </div>
                <h3 className="text-lg font-bold text-brand-navy">{adv.title}</h3>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>


      {/* Short Company Presentation */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <ScrollReveal direction="left" duration={800} className="lg:col-span-6">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy tracking-tight mt-2">
              Et professionelt dansk rengøringsfirma
            </h2>
            <p className="mt-6 text-brand-text-muted leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
              <div className="flex flex-col">
                <span className="text-3xl font-bold text-brand-navy">100%</span>
                <span className="text-xs text-brand-text-muted font-medium uppercase tracking-wider">Tilfredshedsgaranti</span>
              </div>
              <div className="hidden sm:block h-10 w-[1px] bg-brand-border" />
              <hr className="sm:hidden border-neutral-200 w-full" />
              <div className="flex flex-col">
                <span className="text-3xl font-bold text-brand-navy">Svanemærket</span>
                <span className="text-xs text-brand-text-muted font-medium uppercase tracking-wider">Miljøvenlige midler</span>
              </div>
            </div>
            <div className="mt-10">
              <a
                href="#kontakt"
                className="inline-flex items-center gap-2 rounded-lg bg-brand-navy px-6 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-brand-darkblue hover:shadow-lg hover:-translate-y-0.5"
              >
                Kontakt os <i className="fa-solid fa-arrow-right text-xs"></i>
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" duration={800} className="lg:col-span-6 relative">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/kitchen-cleaning-service.jpg"
              alt="Professionel rengøring af køkken"
              className="shadow-xl border border-brand-border w-full object-cover h-[400px]"
            />
          </ScrollReveal>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-white overflow-hidden relative border-t border-neutral-100">
          <div className="mx-auto max-w-7xl px-6 relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-neutral-900 mt-2">
                Hvad vores kunder siger om os
              </h2>
            </div>

            {/* Testimonial Slider */}
            <div className="relative max-w-7xl mx-auto">
              {/* Grid Layout (Responsive: 3 cols on desktop, 2 on tablet, 1 on mobile) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-300">
                {getVisibleTestimonials().map((t, idx) => (
                  <div
                    key={idx}
                    className="border border-neutral-100 bg-neutral-50/50 p-6 md:p-8 flex flex-col justify-between h-full hover:border-neutral-300 transition-all duration-300"
                  >
                    <div>
                      {/* Stars */}
                      <div className="flex items-center gap-1 text-black mb-4">
                        {[...Array(t.rating)].map((_, i) => (
                          <i key={i} className="fa-solid fa-star text-sm"></i>
                        ))}
                      </div>
                      <p className="text-sm text-neutral-600 italic leading-relaxed">
                        "{t.text.da}"
                      </p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-neutral-100">
                      <h4 className="text-sm font-bold text-neutral-900">{t.name}</h4>
                      <p className="text-xs text-neutral-500 mt-0.5">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-center gap-4 mt-10">
                <button
                  onClick={prevTestimonial}
                  className="p-2 text-black hover:text-neutral-600 transition-all duration-200 focus:outline-none"
                  aria-label="Forrige anmeldelse"
                >
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <button
                  onClick={nextTestimonial}
                  className="p-2 text-black hover:text-neutral-600 transition-all duration-200 focus:outline-none"
                  aria-label="Næste anmeldelse"
                >
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Closing CTA & Contact Form Section */}
      <section id="kontakt" className="py-20 bg-brand-light">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Info Column */}
          <ScrollReveal direction="left" duration={800} className="lg:col-span-5 flex flex-col text-left">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy tracking-tight mt-2">
              Lad os tage slæbet for dig
            </h2>
            <p className="mt-4 text-brand-text-muted leading-relaxed">
              Udfyld formularen, eller kontakt os direkte via telefon eller e-mail. Vi udarbejder et uforpligtende tilbud tilpasset dine præcise behov og ønsker.
            </p>

            <div className="mt-8 space-y-6">
              <div className="flex items-center gap-4">
                <div className="text-black shrink-0 text-xl">
                  <i className="fa-solid fa-phone"></i>
                </div>
                <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="text-base text-brand-text-muted hover:text-black transition-colors duration-150 block">
                  {companyInfo.phone}
                </a>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-black shrink-0 text-xl">
                  <i className="fa-solid fa-envelope"></i>
                </div>
                <a href={`mailto:${companyInfo.email}`} className="text-base text-brand-text-muted hover:text-black transition-colors duration-150 block">
                  {companyInfo.email}
                </a>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-black shrink-0 text-xl">
                  <i className="fa-solid fa-clock"></i>
                </div>
                <p className="text-base text-brand-text-muted">
                  {companyInfo.openingHours}
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Form Column */}
          <ScrollReveal direction="right" duration={800} className="lg:col-span-7 w-full">
            <ContactForm />
          </ScrollReveal>
        </div>
      </section>

      {/* Full-width Cleaning in Progress Banner */}
      <section className="relative h-[400px] md:h-[500px] w-full overflow-hidden">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/682e08ab-395d-4160-a58a-7bbf165ed9e3/professional-cleaning-in-action-no-logo.jpg"
          alt="Professionel rengøring i gang"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/90 via-brand-navy/60 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="mx-auto max-w-7xl px-6 w-full">
            <div className="max-w-xl text-left text-white">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mt-4 text-white leading-tight">
                Vi skaber rene og indbydende rammer
              </h2>
              <p className="mt-4 text-base text-slate-200 leading-relaxed">
                Vores erfarne medarbejdere sikrer et skinnende rent resultat hver gang, uanset om det gælder dit private hjem eller din virksomhed.
              </p>
              <div className="mt-8">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-none bg-brand-navy px-6 py-3 text-sm font-bold text-white transition-all duration-200 hover:bg-brand-darkblue hover:shadow-lg hover:-translate-y-0.5"
                >
                  Bestil et uforpligtende tilbud <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxState.isOpen && (
        <Lightbox
          images={beforeAfterImages}
          currentIndex={lightboxState.index}
          onClose={() => setLightboxState({ isOpen: false, index: 0 })}
          onNavigate={(index) => setLightboxState((prev) => ({ ...prev, index }))}
        />
      )}
    </div>
  )
}
